import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import { NAV, HEADER } from "../config-layout";
import AccountPopover from "../common/account-popover";
import { useResponsive } from "../../hooks/useResponsive";
import { bgBlur } from "../../theme/css";
import Logo from "../../modules/auth/pages/Logo";
import SvgColor from "../../components/svg-color/svg-color";
import { useOffSetTop } from "../../hooks/useOffSetTop";
import { useSettingsContext } from "../../components/shared-components/settings";
import SettingsButton from "../common/settings-button";
import LanguagePopover from "../common/language-popover";
import { Input } from "@mui/material";
import OfficeSelection from "../common/office-selection";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const settings = useSettingsContext();
  const isNavHorizontal = settings.themeLayout === "horizontal";
  const isNavMini = settings.themeLayout === "mini";
  const lgUp = useResponsive("up", "lg");
  const offset = useOffSetTop(HEADER.H_DESKTOP);
  const offsetTop = offset && !isNavHorizontal;
  const { t } = useTranslate();

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && (
        <IconButton
          onClick={onOpenNav}
          sx={{
            width: 40,
            height: 40,
            p: 1,
            mr: 0.5,
          }}
        >
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" sx={{ width: 20, height: 20 }} />
        </IconButton>
      )}
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
      >
        <OfficeSelection />
        <Box sx={{ width: '1px', height: 24, bgcolor: 'divider', mx: 3 }} />
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(["height"], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: "background.default",
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
